// SSO utility functions for OAuth PKCE flow

// Constants
export const STORAGE_KEYS = {
  PKCE_CODE_VERIFIER: 'pkce_code_verifier',
  OAUTH_STATE: 'oauth_state',
  SSO_CLIENT_NAME: 'sso_client_name',
  SSO_ORGANIZATION: 'sso_organization',
} as const

const OAUTH_CONFIG = {
  RESPONSE_TYPE: 'code',
  SCOPE: 'openid profile email',
  CODE_CHALLENGE_METHOD: 'S256',
  GRANT_TYPE: 'authorization_code',
} as const

const API_URLS = {
  DEVELOPMENT: 'https://ileg6ficorinq.azurewebsites.net',
  STAGING: 'https://5sfiddus4mmka.azurewebsites.net',
  PRODUCTION: 'https://t7uq4ebbhvpci.azurewebsites.net',
} as const

// Generate random state for CSRF protection
export const generateRandomState = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  )
}

// Generate PKCE code verifier
export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32)
  window.crypto.getRandomValues(array)
  return base64URLEncode(array)
}

// Generate PKCE code challenge
export const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return base64URLEncode(new Uint8Array(digest))
}

// Base64 URL encode function
const base64URLEncode = (array: Uint8Array): string => {
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Get API URL based on environment
const getApiUrl = (): string => {
  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname.includes('localhost')) {
    return 'https://localhost:57180'
  } else if (hostname === 'app-dev.xelto.com') {
    return API_URLS.DEVELOPMENT
  } else if (hostname === 'app-stag.xelto.com') {
    return API_URLS.STAGING
  } else if (hostname === 'app.xelto.com') {
    return API_URLS.PRODUCTION
  } else {
    return API_URLS.DEVELOPMENT
  }
}

// Store OAuth state in both localStorage and sessionStorage for reliability
const storeOAuthData = (
  state: string,
  codeVerifier: string,
  clientName: string,
): void => {
  try {
    const data = {
      [STORAGE_KEYS.PKCE_CODE_VERIFIER]: codeVerifier,
      [STORAGE_KEYS.OAUTH_STATE]: state,
      [STORAGE_KEYS.SSO_CLIENT_NAME]: clientName,
      [STORAGE_KEYS.SSO_ORGANIZATION]: clientName,
    }

    // Store in both localStorage and sessionStorage as backup
    Object.entries(data).forEach(([key, value]) => {
      localStorage.setItem(key, value)
      sessionStorage.setItem(key, value)
    })
  } catch (error) {
    throw new Error(
      'Cannot store OAuth state - localStorage/sessionStorage may be disabled',
    )
  }
}

// Generate OAuth authorization URL
export const generateAuthUrl = async (
  clientName: string,
  redirectUri: string,
): Promise<string> => {
  const state = generateRandomState()
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  // Store OAuth data for later use
  storeOAuthData(state, codeVerifier, clientName)

  // Get current locale from i18next or localStorage
  const currentLocale = localStorage.getItem('i18nextLng') || 'pl'

  const apiUrl = getApiUrl()
  const authUrl =
    `${apiUrl}/connect/authorize?` +
    `client_id=${encodeURIComponent(clientName)}&` +
    `response_type=${OAUTH_CONFIG.RESPONSE_TYPE}&` +
    `scope=${encodeURIComponent(OAUTH_CONFIG.SCOPE)}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `state=${state}&` +
    `code_challenge=${codeChallenge}&` +
    `code_challenge_method=${OAUTH_CONFIG.CODE_CHALLENGE_METHOD}&` +
    `organization=${encodeURIComponent(clientName)}&` +
    `locale=${encodeURIComponent(currentLocale)}`

  return authUrl
}

// Retrieve stored OAuth data
const getStoredOAuthData = () => {
  const codeVerifier =
    localStorage.getItem(STORAGE_KEYS.PKCE_CODE_VERIFIER) ||
    sessionStorage.getItem(STORAGE_KEYS.PKCE_CODE_VERIFIER)
  const clientName =
    localStorage.getItem(STORAGE_KEYS.SSO_CLIENT_NAME) ||
    sessionStorage.getItem(STORAGE_KEYS.SSO_CLIENT_NAME)
  const organization =
    localStorage.getItem(STORAGE_KEYS.SSO_ORGANIZATION) ||
    sessionStorage.getItem(STORAGE_KEYS.SSO_ORGANIZATION)

  return { codeVerifier, clientName, organization }
}

// Exchange authorization code for tokens through direct OAuth server call
export const exchangeCodeForTokens = async (code: string, redirectUri: string) => {
  const { codeVerifier, clientName, organization } = getStoredOAuthData()

  if (!codeVerifier) {
    throw new Error('PKCE code verifier not found in storage')
  }

  if (!clientName) {
    throw new Error('SSO client name not found in storage')
  }

  if (!organization) {
    throw new Error('SSO organization not found in storage')
  }

  // Get current locale from i18next or localStorage
  const currentLocale = localStorage.getItem('i18nextLng') || 'pl'

  const apiUrl = getApiUrl()
  const tokenUrl = `${apiUrl}/connect/token`

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: OAUTH_CONFIG.GRANT_TYPE,
      client_id: clientName,
      code: code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
      organization: organization,
      locale: currentLocale,
    }),
  })

  const tokens = await response.json()

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${tokens.error || 'Unknown error'}`)
  }

  return tokens
}

// Clean up OAuth state from localStorage and sessionStorage
export const cleanupOAuthState = (): void => {
  const keys = Object.values(STORAGE_KEYS)

  keys.forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

// Verify OAuth state parameter (CSRF protection)
export const verifyOAuthState = (receivedState: string): boolean => {
  const storedStateLocal = localStorage.getItem(STORAGE_KEYS.OAUTH_STATE)
  const storedStateSession = sessionStorage.getItem(STORAGE_KEYS.OAUTH_STATE)

  // Extract the original state part (before |org: if present)
  const originalReceivedState = receivedState.split('|')[0]

  // Try both localStorage and sessionStorage, comparing only the original part
  return (
    originalReceivedState === storedStateLocal ||
    originalReceivedState === storedStateSession
  )
}

// Redirect to SSO error page with error details
export const redirectToSSOError = (
  error: string,
  errorDescription?: string,
  organization?: string,
): void => {
  const currentUrl = window.location.origin + '/login'
  const params = new URLSearchParams({
    error,
    ...(errorDescription && { error_description: errorDescription }),
    ...(organization && { organization }),
    original_redirect_uri: currentUrl,
  })

  window.location.href = `/ssoerror?${params.toString()}`
}
